import { useState, useCallback, useEffect } from 'react';
import { ErrorLike } from '@apollo/client';
import {
  WidgetTemplate,
  useWidgetTemplateLazyQuery,
  WidgetTemplateQuery,
} from 'graphql-common';

/** Selected template type from single query (full details) */
export type SelectedWidgetTemplate = NonNullable<WidgetTemplateQuery['data']>;

/** Return type for useSelectedWidgetTemplate hook */
interface UseSelectedWidgetTemplateResult {
  // Data
  selectedTemplateId: string | null;
  /** Template data - initially from sidebar, then replaced with fresh data from query */
  selectedTemplate: WidgetTemplate | SelectedWidgetTemplate | null;

  // Loading state
  loading: boolean;

  // Error state
  error: ErrorLike | undefined;

  // Actions
  /** Select template - stores it immediately and fetches fresh data */
  selectTemplate: (template: WidgetTemplate) => void;
  /** Select template by ID only - fetches template data (used when template isn't in list) */
  selectTemplateById: (id: string) => void;
  clearSelection: () => void;
  /** Update selected template locally (after mutation success) */
  updateSelectedTemplate: (template: WidgetTemplate) => void;
}

/**
 * Hook for managing selected widget template
 * Stores selected template immediately for instant UI, then fetches fresh data
 */
export default function useSelectedWidgetTemplate(): UseSelectedWidgetTemplateResult {
  // Store full template object for instant rendering
  const [selectedTemplate, setSelectedTemplate] = useState<
    WidgetTemplate | SelectedWidgetTemplate | null
  >(null);

  // Lazy query for fetching fresh template details
  // Using 'no-cache' to prevent Apollo from re-fetching when cache updates from mutations
  const [fetchWidgetTemplate, { data, loading, error }] =
    useWidgetTemplateLazyQuery({
      fetchPolicy: 'no-cache',
    });

  // When fresh data arrives, update the selected template
  useEffect(() => {
    if (data?.data) {
      setSelectedTemplate(data.data);
    }
  }, [data]);

  // Select template - store immediately and fetch fresh data
  const selectTemplate = useCallback(
    (template: WidgetTemplate) => {
      setSelectedTemplate(template);
      fetchWidgetTemplate({ variables: { id: template.id } });
    },
    [fetchWidgetTemplate],
  );

  // Select template by ID only - fetches template data
  // Used when template isn't in the loaded list (e.g., after creation with URL param)
  const selectTemplateById = useCallback(
    (id: string) => {
      fetchWidgetTemplate({ variables: { id } });
    },
    [fetchWidgetTemplate],
  );

  const clearSelection = useCallback(() => {
    setSelectedTemplate(null);
  }, []);

  // Update selected template locally (after mutation success, no refetch needed)
  const updateSelectedTemplate = useCallback((template: WidgetTemplate) => {
    setSelectedTemplate(template);
  }, []);

  return {
    selectedTemplateId: selectedTemplate?.id ?? null,
    selectedTemplate,
    loading,
    error,
    selectTemplate,
    selectTemplateById,
    clearSelection,
    updateSelectedTemplate,
  };
}
